import type { FC, HTMLAttributes, PropsWithChildren } from "react";

import { I18n } from "@ui/components/I18n";
import { useTranslations } from "@ui/providers/useTranslations";
import { getFlavorThumbnailSizePx } from "@ui/utils/getFlavorThumbnailSizePx";
import { getOptimizedMagentoImageSrcSet } from "@ui/utils/getOptimizedMagentoImageSrcSet";
import classnames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";

import type { SingleFlavorSelectorItem } from "../../SingleFlavorSelector.types";

import styles from "./FlavorSelectionResult.module.scss";

export type FlavorSelectionResultProps = {
  selectedFlavor?: SingleFlavorSelectorItem;
  flavorsCount: number;
  isSelectorOpen?: boolean;
} & HTMLAttributes<HTMLButtonElement>;

export const FlavorSelectionResult: FC<PropsWithChildren<FlavorSelectionResultProps>> = ({
  selectedFlavor,
  flavorsCount,
  isSelectorOpen = false,
  ...restProps
}) => {
  const { __ } = useTranslations();
  const { showFlavourDescriptionInFlavourSelector = false } = useConfig();
  const thumbnailSizePx = getFlavorThumbnailSizePx();
  const { optimizedImageSrc1x, optimizedImageSrcSet } = getOptimizedMagentoImageSrcSet({
    src: selectedFlavor?.image?.url,
    baseWidth: thumbnailSizePx,
    baseHeight: thumbnailSizePx,
  });

  return (
    <button type="button" className={styles.container} data-testid="flavor-selection-result" {...restProps}>
      {selectedFlavor ? (
        <div className={styles.selectedFlavorContainer}>
          {optimizedImageSrc1x ? (
            <img
              className={styles.image}
              src={optimizedImageSrc1x}
              srcSet={optimizedImageSrcSet}
              alt=""
              loading="lazy"
              decoding="async"
              data-testid="flavor-selection-image"
            />
          ) : (
            <span className={styles.image} data-testid="flavor-selection-image-placeholder" />
          )}

          <div className={styles.headlineContainer}>
            <div className={styles.headlineTitle}>{selectedFlavor.name}</div>

            {showFlavourDescriptionInFlavourSelector && selectedFlavor.description && (
              <div className={styles.headlineSubTitle}>{selectedFlavor.description}</div>
            )}
          </div>
        </div>
      ) : (
        <p className={styles.headlineTitle}>{__("productdetails.sticks.drawer.results.no_selection")}</p>
      )}

      <div className={styles.ctaWithArrow}>
        <span className={styles.ctaLabel}>
          <I18n t="productdetails.sticks.amount">{flavorsCount}</I18n>
        </span>
        <ChevronForwardIcon
          className={classnames(styles.icon, {
            [styles.active]: isSelectorOpen,
          })}
        />
      </div>
    </button>
  );
};
